import { Mail, ExternalLink } from "lucide-react";
import PageHeader from "../components/PageHeader";
import Reveal from "../components/Reveal";

const AUTHORS = [
  {
    initials: "SK",
    name: "Dr Sumanth Kumbargere Nagraj",
    roles: [
      "Clinical Lecturer, Peninsula Dental School, Faculty of Health, University of Plymouth, Plymouth, United Kingdom",
      "Adjunct Professor, Department of Oral Medicine and Radiology, Faculty of Dentistry, Manipal University College Malaysia, Melaka, Malaysia",
    ],
    contribution:
      "Concept development, design of the toolkit, and the research priority setting project with four ethnic groups in Malaysia that informed the toolkit.",
    email: "sumanth.kumbargere@plymouth.ac.uk",
    orcid: "https://orcid.org/0000-0003-1798-3245",
  },
  {
    initials: "PE",
    name: "Dr Prashanti Eachempati",
    roles: [
      "Peninsula Dental School, Faculty of Health, University of Plymouth, Plymouth, United Kingdom",
      "Adjunct Professor, Department of Prosthodontics, Faculty of Dentistry, Manipal University College Malaysia, Melaka, Malaysia",
    ],
    contribution:
      "The participatory research and questionnaire translation projects that informed the toolkit, and the design and hosting of this website.",
    email: "prashanti.eachempati@plymouth.ac.uk",
    orcid: "https://orcid.org/0000-0003-1263-7423",
  },
  {
    initials: "SU",
    name: "Dr Seilin Uhm",
    roles: [
      "Research Fellow, Department of Psychiatry, Faculty of Medicine, University of Southampton, Southampton, United Kingdom",
    ],
    contribution:
      "Design and revision of the toolkit content, and the research agenda setting work with service users and healthcare professionals that informed the toolkit.",
    email: "s.s.uhm@soton.ac.uk",
    orcid: "https://orcid.org/0000-0002-9847-8381",
  },
  {
    initials: "MN",
    name: "Professor Mona Nasser",
    roles: [
      "Professor in Clinical Epidemiology and Oral Health Research, Peninsula Dental School, Faculty of Health, University of Plymouth, Plymouth, United Kingdom",
    ],
    contribution: "Concept development, design of the toolkit, and revision of the toolkit content.",
    email: "mona.nasser@plymouth.ac.uk",
    orcid: "https://orcid.org/0000-0003-2220-6491",
  },
];

const PROJECTS = [
  {
    n: "1",
    title: "Identifying research priorities",
    body: "An equity based oral health research priority setting exercise with four ethnic groups of Malaysia. Participants were interviewed in their own languages, supported by eight translators of Malay, Chinese and Indian ethnicities.",
  },
  {
    n: "2",
    title: "Participatory research method",
    body: "Photo elicitation workshops exploring how different ethnic groups in Malaysia perceive uncertainties in oral health treatment decisions, conducted in Bahasa Malaysia, Mandarin and Tamil.",
  },
  {
    n: "3",
    title: "Translation of a questionnaire",
    body: "Translation and cross cultural adaptation of the Intolerance of Uncertainty questionnaire into Bahasa Malaysia, with expert input from each ethnic group.",
  },
  {
    n: "4",
    title: "Research agenda setting",
    body: "The James Lind Alliance Preterm Birth Priority Setting Partnership in the United Kingdom and Ireland, which used linguistic and cultural adaptations in targeted outreach so that traditionally excluded communities were included.",
  },
];

const AuthorCard = ({ a, index }) => (
  <article className="flex h-full flex-col rounded-lg border border-melt-grey bg-white p-8" data-testid={`author-card-${index}`}>
    <div className="flex items-center gap-4">
      <span
        aria-hidden="true"
        className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full bg-melt-indigo font-serif text-xl font-semibold text-melt-off-white"
      >
        {a.initials}
      </span>
      <h3 className="font-serif text-xl font-semibold leading-tight text-melt-indigo">{a.name}</h3>
    </div>
    <div className="mt-5 space-y-1.5">
      {a.roles.map((r, i) => (
        <p key={i} className="text-[15px] leading-snug text-melt-indigo/70">{r}</p>
      ))}
    </div>
    <p className="mt-4 flex-1 text-[15px] text-melt-indigo/80">
      <span className="font-semibold text-melt-indigo">Contribution: </span>
      {a.contribution}
    </p>
    <div className="mt-6 flex flex-wrap gap-4 border-t border-melt-grey pt-5">
      <a
        href={`mailto:${a.email}`}
        className="inline-flex items-center gap-2 text-sm font-medium text-melt-teal hover:underline"
        data-testid={`author-email-${index}`}
      >
        <Mail size={16} aria-hidden="true" />
        {a.email}
      </a>
      <a
        href={a.orcid}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-2 text-sm font-medium text-melt-teal hover:underline"
        data-testid={`author-orcid-${index}`}
      >
        <ExternalLink size={16} aria-hidden="true" />
        ORCID
        <span className="sr-only">(opens in a new tab)</span>
      </a>
    </div>
  </article>
);

export default function About() {
  return (
    <>
      <PageHeader
        eyebrow="About us"
        title="The team behind MELT"
        intro="MELT was developed by a team of researchers working across dentistry, clinical epidemiology, psychiatry and public involvement in the United Kingdom and Malaysia. The team brings together experience of research priority setting, participatory methods, cross cultural questionnaire adaptation and the engagement of communities that are frequently underrepresented in health research."
        testId="about-header"
      />

      {/* Author profiles */}
      <section className="mx-auto max-w-6xl px-5 py-20 md:px-8 md:py-24" aria-labelledby="authors-heading">
        <Reveal>
          <h2 id="authors-heading" className="font-serif text-3xl font-semibold md:text-4xl">Authors</h2>
        </Reveal>
        <div className="mt-10 grid gap-6 md:grid-cols-2">
          {AUTHORS.map((a, i) => (
            <Reveal key={a.name} delay={(i % 2) * 0.1}>
              <AuthorCard a={a} index={i} />
            </Reveal>
          ))}
        </div>
      </section>

      {/* Projects */}
      <section className="woven-texture-light border-y border-melt-grey" aria-labelledby="projects-heading">
        <div className="mx-auto max-w-4xl px-5 py-20 md:px-8 md:py-24">
          <Reveal>
            <h2 id="projects-heading" className="font-serif text-3xl font-semibold md:text-4xl">The projects behind MELT</h2>
            <p className="mt-4 text-lg text-melt-indigo/80">
              Four research projects, conducted in Malaysia and the United Kingdom, informed the toolkit.
            </p>
          </Reveal>
          <div className="mt-10 space-y-5">
            {PROJECTS.map((p, i) => (
              <Reveal key={p.n} delay={i * 0.06}>
                <article className="flex gap-6 rounded-lg border border-melt-grey bg-white p-7" data-testid={`project-card-${p.n}`}>
                  <span className="font-serif text-3xl font-semibold leading-none text-melt-amber">{p.n}</span>
                  <div>
                    <h3 className="font-serif text-lg font-semibold text-melt-indigo">{p.title}</h3>
                    <p className="mt-2 text-base text-melt-indigo/80">{p.body}</p>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Review & Acknowledgements */}
      <section className="mx-auto max-w-4xl px-5 py-20 md:px-8 md:py-24">
        <div className="grid gap-12 md:grid-cols-2">
          <Reveal>
            <h2 className="font-serif text-2xl font-semibold md:text-3xl">Review and refinement</h2>
            <p className="mt-4 text-base text-melt-indigo/80">
              Members of the Cochrane Priority Setting Methods Group reviewed the draft guidance materials
              across four meetings between August and September 2022. The toolkit was further refined
              following presentations at national and international research meetings.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="font-serif text-2xl font-semibold md:text-3xl">Acknowledgements</h2>
            <p className="mt-4 text-base text-melt-indigo/80">
              We thank colleagues at the University of Plymouth and at the Faculty of Dentistry, Manipal
              University College Malaysia. We are grateful to the translators and community participants
              who contributed to the four projects.
            </p>
          </Reveal>
        </div>
      </section>
    </>
  );
}
